<template>
    <div class="c-sow" :style="wrapStyle">
        <div :style="innerStyle">
            <div :style="headerStyle">
                <div :style="eyebrowStyle">SOW · {{ currentBrand || agencyName }}</div>
                <h1 :style="h1Style">Let's record the new SOW.</h1>
                <p :style="leadStyle">
                    Tell us about the statement of work — title, validity window,
                    and whether it's signed. You can always upload the document later
                    if you don't have it on hand.
                </p>
            </div>

            <!-- ===== Top card: identity + dates + deal mode ===== -->
            <CCard :extra-style="topCardStyle">
                <div :style="topGridStyle">
                    <div :style="topLeftStyle">
                        <div>
                            <label :style="fieldLabelStyle">
                                Title <span :style="requiredMark">*</span>
                            </label>
                            <div :style="inputWrapStyle">
                                <input
                                    type="text"
                                    id="sow_title"
                                    v-model="new_sow.title"
                                    name="sow_title"
                                    :style="bareInputStyle"
                                />
                            </div>
                            <span v-if="errors.title" :style="fieldErrorStyle">{{ errors.title }}</span>
                        </div>

                        <div :style="dealModeBlockStyle">
                            <div :style="fieldLabelStyle">Which deal does this SOW belong to?</div>
                            <div :style="dealModeGridStyle">
                                <label
                                    for="is_new_deal_false"
                                    :style="cRadioStyle(!is_new_deal)"
                                >
                                    <input
                                        type="radio"
                                        name="is_new_deal"
                                        id="is_new_deal_false"
                                        :checked="!is_new_deal"
                                        :style="srOnly"
                                        @change="is_new_deal = false"
                                    />
                                    <span :style="cRadioDotStyle(!is_new_deal)">
                                        <span v-if="!is_new_deal" :style="cRadioInnerStyle" />
                                    </span>
                                    <div>
                                        <div :style="cRadioLabelStyle">Current deal</div>
                                        <div :style="cRadioSubStyle">Legal terms unchanged, new SOW only</div>
                                    </div>
                                </label>
                                <label
                                    for="is_new_deal_true"
                                    :style="cRadioStyle(is_new_deal)"
                                >
                                    <input
                                        type="radio"
                                        name="is_new_deal"
                                        id="is_new_deal_true"
                                        :checked="is_new_deal"
                                        :style="srOnly"
                                        @change="is_new_deal = true"
                                    />
                                    <span :style="cRadioDotStyle(is_new_deal)">
                                        <span v-if="is_new_deal" :style="cRadioInnerStyle" />
                                    </span>
                                    <div>
                                        <div :style="cRadioLabelStyle">Create new deal</div>
                                        <div :style="cRadioSubStyle">New or updated legal terms signed</div>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div :style="topRightStyle">
                        <div>
                            <label :style="fieldLabelStyle">
                                Activation date <span :style="requiredMark">*</span>
                            </label>
                            <div
                                class="form-field form-field--datepicker"
                                ref="datepicker"
                                :style="datepickerWrapStyle"
                            >
                                <input
                                    type="text"
                                    id="sow_date"
                                    v-model="new_sow.date"
                                    name="sow_date"
                                    placeholder="mm/dd/yyyy"
                                    :style="datepickerInputStyle"
                                    @changeDate="onChangeDatePicker"
                                />
                                <button type="button" class="btn btn--icon" :style="datepickerBtnStyle">
                                    <i class="icon icon-calendar"></i>
                                </button>
                            </div>
                            <span v-if="errors.date" :style="fieldErrorStyle">{{ errors.date }}</span>
                        </div>

                        <div>
                            <label :style="fieldLabelStyle">
                                Valid until <span :style="optionalBadge">optional</span>
                            </label>
                            <div
                                class="form-field form-field--datepicker"
                                ref="valid_datepicker"
                                :style="datepickerWrapStyle"
                            >
                                <input
                                    type="text"
                                    id="valid_date"
                                    v-model="new_sow.valid_date"
                                    name="valid_date"
                                    placeholder="mm/dd/yyyy"
                                    :style="datepickerInputStyle"
                                    @changeDate="onChangeValidDatePicker"
                                />
                                <button type="button" class="btn btn--icon" :style="datepickerBtnStyle">
                                    <i class="icon icon-calendar"></i>
                                </button>
                            </div>
                            <span v-if="errors.valid_date" :style="fieldErrorStyle">{{ errors.valid_date }}</span>
                        </div>
                    </div>
                </div>
            </CCard>

            <!-- ===== Two parallel cards: Signed vs Not signed ===== -->
            <div :style="dualCardGridStyle">
                <!-- Signed -->
                <CCard :extra-style="signedCardStyle(is_has_signed_sow === 'yes')">
                    <div
                        :style="signedHeaderStyle"
                        @click="is_has_signed_sow = 'yes'"
                    >
                        <span :style="cRadioDotStyle(is_has_signed_sow === 'yes')">
                            <span v-if="is_has_signed_sow === 'yes'" :style="cRadioInnerStyle" />
                        </span>
                        <div>
                            <div :style="signedTitleStyle">I have a signed SOW</div>
                            <div :style="signedSubStyle">Attach the document or link, or upload it later.</div>
                        </div>
                        <!-- Hidden radio for form-contract compat -->
                        <input
                            type="radio"
                            name="is_has_signed_sow"
                            id="is_has_signed_sow_true"
                            :checked="is_has_signed_sow === 'yes'"
                            :style="srOnly"
                            @change="is_has_signed_sow = 'yes'"
                        />
                    </div>

                    <div v-if="is_has_signed_sow === 'yes'" :style="signedRowsStyle">
                        <!-- File row -->
                        <div :style="proofRowStyle(is_file_option === true || (is_file_option && is_file_option !== 'later' && is_file_option !== false))">
                            <span
                                :style="cRadioDotStyle(is_file_option === true || (is_file_option && is_file_option !== 'later' && is_file_option !== false))"
                                @click="is_file_option = true"
                            >
                                <span
                                    v-if="is_file_option === true || (is_file_option && is_file_option !== 'later' && is_file_option !== false)"
                                    :style="cRadioInnerStyle"
                                />
                            </span>
                            <div :style="proofRowBodyStyle">
                                <div :style="proofRowLabelStyle">File</div>
                                <div v-if="(is_file_option === true) && new_sow.filename" :style="proofRowMetaStyle">
                                    {{ new_sow.filename }}
                                    <a href="#" :style="proofRowRemoveStyle" @click.prevent="new_sow.filename = ''; new_sow.file = null;">remove</a>
                                </div>
                                <div v-else :style="proofRowMetaStyle">PDF, DOCX up to 20 MB</div>
                            </div>
                            <!-- Hidden radio for form-contract compat -->
                            <input
                                type="radio"
                                name="is_file"
                                id="is_file_true"
                                :checked="is_file_option && is_file_option !== 'later'"
                                :style="srOnly"
                                @change="is_file_option = true"
                            />
                            <!-- File picker (preserved $refs.filepicker + $refs.fileInput contract) -->
                            <div
                                class="form-field form-field--file"
                                ref="filepicker"
                                :style="filePickerWrapStyle"
                            >
                                <input
                                    type="file"
                                    id="agreement"
                                    :disabled="!is_file_option_computed"
                                    name="agreement"
                                    hidden
                                    ref="fileInput"
                                    @input="pickFile"
                                    @change="pickFile"
                                />
                                <button
                                    type="button"
                                    class="btn btn--file"
                                    :style="filePickerBtnStyle"
                                >
                                    {{ new_sow.filename && is_file_option === true ? 'Replace' : 'Choose file' }} +
                                </button>
                            </div>
                            <span v-if="errors.file" :style="fieldErrorStyle">{{ errors.file }}</span>
                        </div>

                        <!-- URL row -->
                        <div :style="proofRowStyle(is_file_option === false)">
                            <span
                                :style="cRadioDotStyle(is_file_option === false)"
                                @click="is_file_option = false"
                            >
                                <span v-if="is_file_option === false" :style="cRadioInnerStyle" />
                            </span>
                            <div :style="proofRowBodyStyle">
                                <div :style="proofRowLabelStyle">URL</div>
                                <div v-if="is_file_option !== false" :style="proofRowMetaStyle">
                                    Paste a link to the signed SOW
                                </div>
                                <div v-else :style="urlInputWrapStyle">
                                    <input
                                        type="text"
                                        id="sow_url"
                                        :disabled="is_file_option_computed"
                                        v-model="new_sow.url"
                                        name="sow_url"
                                        placeholder="https://docs.google.com/…"
                                        :style="bareInputStyle"
                                    />
                                </div>
                                <span v-if="errors.url" :style="fieldErrorStyle">{{ errors.url }}</span>
                            </div>
                            <input
                                type="radio"
                                name="is_file"
                                id="is_file_false"
                                :checked="is_file_option === false"
                                :style="srOnly"
                                @change="is_file_option = false"
                            />
                        </div>

                        <!-- Later row -->
                        <div
                            :style="proofRowStyle(is_file_option === 'later')"
                            @click="is_file_option = 'later'"
                        >
                            <span :style="cRadioDotStyle(is_file_option === 'later')">
                                <span v-if="is_file_option === 'later'" :style="cRadioInnerStyle" />
                            </span>
                            <div :style="proofRowBodyStyle">
                                <div :style="proofRowLabelStyle">Upload it later</div>
                                <div :style="proofRowMetaStyle">We'll remind you next period.</div>
                            </div>
                            <input
                                type="radio"
                                name="is_upload_later"
                                id="is_upload_later"
                                :checked="is_file_option === 'later'"
                                :style="srOnly"
                                @change="is_file_option = 'later'"
                            />
                        </div>
                    </div>
                </CCard>

                <!-- Unsigned -->
                <CCard :extra-style="signedCardStyle(is_has_signed_sow === 'no')">
                    <div
                        :style="signedHeaderStyle"
                        @click="is_has_signed_sow = 'no'"
                    >
                        <span :style="cRadioDotStyle(is_has_signed_sow === 'no')">
                            <span v-if="is_has_signed_sow === 'no'" :style="cRadioInnerStyle" />
                        </span>
                        <div>
                            <div :style="signedTitleStyle">No signed SOW yet</div>
                            <div :style="signedSubStyle">Help us understand why so we can follow up.</div>
                        </div>
                        <input
                            type="radio"
                            name="is_has_signed_sow"
                            id="is_has_signed_sow_false"
                            :checked="is_has_signed_sow === 'no'"
                            :style="srOnly"
                            @change="is_has_signed_sow = 'no'"
                        />
                    </div>

                    <div v-if="is_has_signed_sow === 'no'" :style="explanationFieldStyle">
                        <label :style="fieldLabelStyle">
                            Explanation <span :style="requiredMark">*</span>
                        </label>
                        <textarea
                            id="explanation"
                            v-model="description"
                            name="explanation"
                            :style="textareaStyle"
                            placeholder="e.g. Client agreed verbally, paperwork in legal review…"
                        ></textarea>
                    </div>
                </CCard>
            </div>

            <!-- ===== Bottom nav ===== -->
            <div :style="navStyle">
                <CBtn
                    ghost
                    @click="onChangeSection(SECTIONS.DO_YOU_HAVE_UPSELL, SECTIONS.EDIT_SALE_LINE_ITEM)"
                >
                    ← Previous
                </CBtn>
                <span :style="navHintStyle">autosaved as you go</span>
                <CBtn
                    primary
                    big
                    :disabled="!canSubmit || isLoading"
                    @click="onSaveSow"
                >
                    Next →
                </CBtn>
            </div>

            <div v-if="isLoading" :style="loaderStyle">
                <clip-loader :loading="isLoading" color="#ec4657" size="45px" />
            </div>
        </div>
    </div>
</template>

<script>
import ClipLoader from "vue-spinner/src/ClipLoader";
import moment from "moment";

import { Datepicker } from "vanillajs-datepicker";
import { SECTIONS } from "../consts";
import axios from "axios";
import { scrollToTop } from "../../../helpers/_scrollToTop";
import { reInitAllTooltips } from "../../../helpers/_tooltip";

import CCard from "../c-design/CCard.vue";
import CBtn from "../c-design/CBtn.vue";
import viewportMixin from "../c-design/useViewport";
import {
    C_BG,
    C_INK,
    C_INK2,
    C_MUTED,
    C_HAIR,
    C_CREAM,
    C_RED,
    FONT_DISPLAY,
    FONT_BODY,
    navMobileStickyStyle,
} from "../c-design/tokens";

export default {
    name: "Wizard_SOW",
    mixins: [viewportMixin],
    props: ["onChangeSection"],
    components: {
        ClipLoader,
        CCard,
        CBtn,
    },
    data() {
        return {
            SECTIONS,
            isLoading: false,
            is_has_signed_sow: "yes",
            // Pre-Stage-16 this was set implicitly by getData() — now declared
            // explicitly so Vue reactivity picks it up from first render and
            // the deal-mode CRadio pair has a defined default (false = current).
            is_new_deal: false,
            sow_selected: null,
            new_sow: {
                title: "",
                date: "",
                file: null,
                url: "",
                filename: "",
                valid_date: "",
            },
            description: "",
            datepicker_options: {
                format: "mm/dd/yyyy",
                pickLevel: 0,
                autohide: true,
            },
            is_file_option: true,
            is_upload_later: false,
            errors: {
                title: null,
                date: null,
                valid_date: null,
                file: null,
                url: null,
                type: null,
                description: null,
            },
            urlRegex:
                /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/,
        };
    },

    computed: {
        prevSection() {
            return this.$store.getters.getPrevComponent;
        },

        currentBrand() {
            return this.$store.getters.getCurrentBrandName;
        },

        agencyName() {
            return this.$store.getters.getAgencyName;
        },

        is_file_option_computed() {
            if (this.is_has_signed_sow) {
                if (this.is_file_option) {
                    return true;
                } else {
                    return false;
                }
            } else {
                return true;
            }
        },

        getCurrentDealId() {
            return (
                this.$store.getters.getSalesPages(
                    this.$store.getters.getDealSalesIndex
                ).deal_id || null
            );
        },

        sowIndex() {
            return this.$store.getters.getCurrentIndexSOW;
        },

        upsellIndex() {
            return this.$store.getters.getCurrentIndexSOW;
        },

        isEditUpsell() {
            return this.$store.getters.getIsEditUpsell;
        },
        currentDate() {
            return moment().format("MMDDYYYY");
        },
        // ===== Submit gating (was inline v-if on the Next button) =====
        canSubmit() {
            if (this.is_file_option === 'later') return true;
            if (this.is_has_signed_sow === 'yes') {
                if (this.is_file_option === true) return Boolean(this.new_sow.filename);
                if (this.is_file_option === false) return Boolean(this.new_sow.url);
                return false;
            }
            if (this.is_has_signed_sow === 'no') {
                return (this.description || '').length >= 5;
            }
            return false;
        },
        // ===== c-design style maps =====
        wrapStyle() {
            return {
                background: C_BG,
                minHeight: '70vh',
                padding: this.isMobile ? '14px 12px 110px' : '24px 16px 40px',
            };
        },
        innerStyle() {
            return { maxWidth: '980px', margin: '0 auto' };
        },
        headerStyle() {
            return {
                marginBottom: this.isMobile ? '14px' : '20px',
                padding: this.isMobile ? '0 4px' : '0 8px',
            };
        },
        eyebrowStyle() {
            return {
                fontSize: '11px', color: C_MUTED, letterSpacing: '0.8px',
                textTransform: 'uppercase', fontWeight: 700,
            };
        },
        h1Style() {
            return {
                margin: '8px 0 8px', fontFamily: FONT_DISPLAY,
                fontSize: this.isMobile ? '28px' : '36px',
                fontWeight: 700,
                letterSpacing: this.isMobile ? '-0.8px' : '-1px',
                lineHeight: 1.1, color: C_INK,
            };
        },
        leadStyle() {
            return {
                margin: 0,
                fontSize: this.isMobile ? '13.5px' : '15px',
                color: C_MUTED,
                lineHeight: 1.55,
                maxWidth: this.isMobile ? 'none' : '640px',
            };
        },
        topCardStyle() {
            return {
                marginTop: '6px',
                marginBottom: this.isMobile ? '12px' : '16px',
                padding: this.isMobile ? '22px' : '28px 32px',
            };
        },
        topGridStyle() {
            return {
                display: 'grid',
                gridTemplateColumns: this.isMobile ? '1fr' : '1fr 280px',
                gap: this.isMobile ? '18px' : '24px',
                alignItems: 'start',
            };
        },
        topLeftStyle() {
            return { display: 'flex', flexDirection: 'column', gap: '16px' };
        },
        topRightStyle() {
            return { display: 'flex', flexDirection: 'column', gap: '16px' };
        },
        fieldLabelStyle() {
            return {
                display: 'flex', alignItems: 'baseline', gap: '8px',
                marginBottom: '7px', fontSize: '12px', color: C_INK2,
                fontWeight: 600, letterSpacing: '0.1px',
            };
        },
        requiredMark() {
            return { color: C_RED, marginLeft: '3px' };
        },
        optionalBadge() {
            return { fontSize: '11px', color: C_MUTED, fontWeight: 400 };
        },
        inputWrapStyle() {
            return {
                display: 'flex', alignItems: 'center',
                border: `1.5px solid ${C_HAIR}`, borderRadius: '14px',
                background: C_CREAM, padding: '0 14px',
            };
        },
        bareInputStyle() {
            return {
                flex: 1, border: 'none', outline: 'none',
                background: 'transparent', padding: '14px 0',
                fontSize: '16px', fontFamily: FONT_BODY,
                fontWeight: 500, color: C_INK, width: '100%',
            };
        },
        urlInputWrapStyle() {
            return {
                marginTop: '6px',
                display: 'flex', alignItems: 'center',
                border: `1.5px solid ${C_HAIR}`, borderRadius: '14px',
                background: C_CREAM, padding: '0 12px',
            };
        },
        datepickerWrapStyle() {
            return {
                display: 'flex', alignItems: 'center',
                border: `1.5px solid ${C_HAIR}`, borderRadius: '14px',
                background: C_CREAM, padding: '0 12px',
            };
        },
        datepickerInputStyle() {
            return {
                flex: 1, border: 'none', outline: 'none',
                background: 'transparent', padding: '14px 0',
                fontSize: '14px', fontFamily: FONT_BODY,
                color: C_INK, width: '100%',
            };
        },
        datepickerBtnStyle() {
            return {
                background: 'transparent', border: 'none',
                padding: '6px 4px', margin: '0 0 0 4px',
                fontSize: '16px', color: C_MUTED, cursor: 'pointer',
            };
        },
        srOnly() {
            return {
                position: 'absolute', width: '1px', height: '1px',
                margin: '-1px', overflow: 'hidden',
                clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: '0',
            };
        },
        dealModeBlockStyle() {
            return { display: 'flex', flexDirection: 'column', gap: '10px' };
        },
        dealModeGridStyle() {
            return {
                display: 'grid',
                gridTemplateColumns: this.isMobile ? '1fr' : '1fr 1fr',
                gap: '10px',
            };
        },
        cRadioInnerStyle() {
            return {
                width: '11px', height: '11px', borderRadius: '50%',
                background: C_INK, display: 'inline-block',
            };
        },
        cRadioLabelStyle() {
            return {
                fontSize: '14px', fontWeight: 600, color: C_INK,
            };
        },
        cRadioSubStyle() {
            return {
                fontSize: '12px', color: C_MUTED, marginTop: '2px',
                lineHeight: 1.45,
            };
        },
        dualCardGridStyle() {
            return {
                display: 'grid',
                gridTemplateColumns: this.isMobile ? '1fr' : '1fr 1fr',
                gap: this.isMobile ? '12px' : '16px',
                marginTop: '4px',
            };
        },
        signedHeaderStyle() {
            return {
                display: 'flex', alignItems: 'flex-start', gap: '12px',
                cursor: 'pointer',
            };
        },
        signedTitleStyle() {
            return {
                fontFamily: FONT_DISPLAY, fontSize: '17px', fontWeight: 700,
                letterSpacing: '-0.2px', color: C_INK,
            };
        },
        signedSubStyle() {
            return {
                fontSize: '12px', color: C_MUTED, marginTop: '1px',
            };
        },
        signedRowsStyle() {
            return {
                marginTop: '18px',
                display: 'flex', flexDirection: 'column', gap: '10px',
            };
        },
        proofRowBodyStyle() {
            return { flex: 1, minWidth: 0 };
        },
        proofRowLabelStyle() {
            return { fontSize: '13px', fontWeight: 600, color: C_INK };
        },
        proofRowMetaStyle() {
            return {
                fontSize: '12px', color: C_MUTED, marginTop: '2px',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            };
        },
        proofRowRemoveStyle() {
            return {
                color: C_INK, marginLeft: '6px',
                textDecoration: 'underline', textUnderlineOffset: '2px',
            };
        },
        filePickerWrapStyle() {
            return { display: 'flex', alignItems: 'center', gap: '8px' };
        },
        filePickerBtnStyle() {
            return {
                padding: '8px 14px', borderRadius: '10px',
                border: `1.5px solid ${C_HAIR}`, background: '#fff',
                fontFamily: FONT_BODY, fontSize: '13px', fontWeight: 600,
                color: C_INK, cursor: 'pointer',
            };
        },
        explanationFieldStyle() {
            return { marginTop: '14px' };
        },
        textareaStyle() {
            return {
                width: '100%', minHeight: '140px', padding: '14px',
                border: `1.5px solid ${C_HAIR}`, borderRadius: '14px',
                background: C_CREAM, resize: 'vertical',
                fontFamily: FONT_BODY, fontSize: '14px',
                color: C_INK, outline: 'none', lineHeight: 1.55,
            };
        },
        fieldErrorStyle() {
            return {
                display: 'block', marginTop: '6px',
                fontSize: '12px', color: C_RED, fontWeight: 500,
            };
        },
        navStyle() {
            return navMobileStickyStyle(this.isMobile, {
                marginTop: '14px', padding: '16px 24px',
                background: '#fff', borderRadius: '16px',
                border: `1px solid ${C_HAIR}`,
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: '16px',
            });
        },
        navHintStyle() {
            return { fontSize: '12px', color: C_MUTED, fontStyle: 'italic' };
        },
        loaderStyle() {
            return {
                display: 'flex', alignItems: 'center',
                justifyContent: 'center', padding: '32px',
            };
        },
    },

    mounted() {
        this.initDatePicker();
        this.initValidDatePicker();
        this.initFilePicker();

        this.getData();

        scrollToTop();

        setTimeout(() => {
            reInitAllTooltips();
        }, 750);
    },

    methods: {
        // ===== c-design helpers =====
        cRadioStyle(checked) {
            return {
                display: 'flex', alignItems: 'flex-start', gap: '12px',
                padding: '14px 16px',
                background: checked ? C_CREAM : '#fff',
                border: `1.5px solid ${checked ? C_INK : C_HAIR}`,
                borderRadius: '14px', cursor: 'pointer',
                transition: 'all 120ms',
                position: 'relative',
            };
        },
        cRadioDotStyle(checked) {
            return {
                width: '22px', height: '22px', borderRadius: '50%',
                border: `1.5px solid ${checked ? C_INK : C_HAIR}`,
                background: '#fff', flexShrink: 0,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                marginTop: '1px',
                cursor: 'pointer',
            };
        },
        signedCardStyle(active) {
            return {
                padding: '24px 26px',
                border: `2px solid ${active ? C_INK : 'transparent'}`,
                cursor: 'pointer',
            };
        },
        proofRowStyle(active) {
            return {
                display: 'grid', gridTemplateColumns: '22px 1fr auto', gap: '12px',
                alignItems: 'center',
                padding: '12px 14px',
                background: active ? C_CREAM : '#fff',
                border: `1.5px solid ${active ? C_INK : C_HAIR}`,
                borderRadius: '12px',
                cursor: 'pointer',
                position: 'relative',
            };
        },

        async getData() {
            await axios
                .post("/api/wizard/getList", {
                    page: "new_sow",
                    deal_id: this.getCurrentDealId,
                    sow_index: this.isEditUpsell
                        ? this.sowIndex
                        : this.sowIndex + 1,
                })
                .then((res) => {
                    this.new_sow.title = res.data.page.sow_title;
                    if (res.data.page.sow_draft) {
                        this.new_sow.date = res.data.page.sow_draft.date;
                        this.new_sow.url = res.data.page.sow_draft.url;
                        this.new_sow.is_upload_later =
                            res.data.page.sow_draft.is_upload_later;
                        this.new_sow.description =
                            res.data.page.sow_draft.description;
                        this.new_sow.title = res.data.page.sow_draft.name;
                        this.new_sow.valid_date =
                            res.data.page.sow_draft.valid_date;
                        if (res.data.page.sow_draft.type === "file") {
                            this.new_sow.filename =
                                res.data.page.sow_draft.file;
                        } else {
                            this.is_file_option = false;
                        }

                        if (res.data.page.sow_draft.is_upload_later) {
                            this.is_has_signed_sow = "yes";
                            this.is_file_option = "later";
                        }

                        this.is_new_deal = res.data.page.is_new_deal || false;
                    }
                })
                .catch((err) => console.log("err getData sow", err));
        },
        initDatePicker() {
            const node__datepicker = this.$refs.datepicker;

            if (!node__datepicker) {
                return;
            }

            let datepicker = null;
            const input = node__datepicker.querySelector("input");
            const button = node__datepicker.querySelector("button");

            if (input) {
                datepicker = new Datepicker(input, {
                    ...this.datepicker_options,
                });
            }

            if (button) {
                button.addEventListener("click", () => {
                    if (!datepicker.active) {
                        datepicker.show();
                    }
                });
            }
        },
        initValidDatePicker() {
            const node__datepicker = this.$refs.valid_datepicker;

            if (!node__datepicker) {
                return;
            }

            let datepicker = null;
            const input = node__datepicker.querySelector("input");
            const button = node__datepicker.querySelector("button");

            if (input) {
                datepicker = new Datepicker(input, {
                    ...this.datepicker_options,
                });
            }

            if (button) {
                button.addEventListener("click", () => {
                    if (!datepicker.active) {
                        datepicker.show();
                    }
                });
            }
        },

        onChangeDatePicker(e) {
            const date = e.target.value;

            if (date) {
                this.new_sow.date = date;
            }
        },
        onChangeValidDatePicker(e) {
            const date = e.target.value;

            if (date) {
                this.new_sow.valid_date = date;
            }
        },

        initFilePicker() {
            const inputField = this.$refs.filepicker;

            if (!inputField) {
                return;
            }

            const button = inputField.querySelector("button");
            const input = inputField.querySelector("input");
            const filenameNode = inputField.querySelector("span.filename");

            if (button && input) {
                button.addEventListener("click", () => {
                    input.click();
                });
            }

            input.addEventListener("change", () => {
                let file = input.files;
                if (file && file[0]) {
                    const filename = file[0].name;
                    this.new_sow.filename = filename;
                    //filenameNode.innerHTML = filename;
                    this.new_sow.file = file[0];
                }
            });
        },
        pickFile() {
            let input = this.$refs.fileInput;
            let file = input.files;
            if (file && file[0]) {
                let reader = new FileReader();
                reader.readAsDataURL(file[0]);
                this.new_sow.file = file[0];
            }
        },

        validateFields() {
            if (this.is_file_option === "later") return;

            this.errors.date = null;
            this.errors.title = null;
            this.errors.file = null;
            this.errors.type = null;
            this.errors.url = null;
            this.errors.valid_date = null;

            if (!this.new_sow.title) {
                this.errors.title = "Required";
            }

            if (!this.new_sow.date) {
                this.errors.date = "Required";
            }
            if (
                !this.is_file_option &&
                this.new_sow.url &&
                !this.urlRegex.test(this.new_sow.url)
            ) {
                this.errors.url = "Invalid URL";
            }
            if (
                this.new_sow.date &&
                this.new_sow.valid_date &&
                new Date(this.new_sow.date) > new Date(this.new_sow.valid_date)
            ) {
                this.errors.valid_date = "Invalid valid date";
            }
        },

        onSaveSow() {
            try {
                this.validateFields();

                if (
                    this.is_has_signed_sow &&
                    (this.errors.date ||
                        this.errors.file ||
                        this.errors.type ||
                        this.errors.url ||
                        this.errors.valid_date)
                ) {
                    return;
                }

                if (
                    !this.is_has_signed_sow &&
                    (this.errors.title ||
                        this.errors.date ||
                        this.errors.valid_date)
                ) {
                    return;
                }
                this.isLoading = true;
                const formData = new FormData();

                const data = {
                    name: this.new_sow.title,
                    date: this.new_sow.date === null ? "" : this.new_sow.date,
                    url: this.new_sow.url ? this.new_sow.url : "",
                    description: this.description,
                    deal_id: this.getCurrentDealId,
                    type: this.is_file_option ? "file" : "url",
                    is_upload_later: this.is_file_option === "later",
                    is_file: this.is_file_option,
                    valid_date: this.new_sow.valid_date,
                    sow_index: this.isEditUpsell
                        ? this.sowIndex
                        : this.sowIndex + 1,
                    is_new_deal: this.is_new_deal,
                };
                formData.append("action", "createSow");
                formData.append("data", JSON.stringify(data));
                formData.append(
                    "file",
                    this.new_sow.file === null ? "" : this.new_sow.file
                );

                axios
                    .post("api/wizard/updateDraft", formData, {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    })
                    .then(() => {
                        if (!this.isEditUpsell) {
                            this.$store.commit(
                                "setIndexSOW",
                                this.sowIndex + 1
                            );
                            this.$store.commit(
                                "setIndexUpsell",
                                this.upsellIndex + 1
                            );
                        }
                        //this.$toast.success("SOW has been created!");
                        this.onChangeSection(SECTIONS.UPSELL, SECTIONS.SOW);
                    })
                    .catch((err) => {
                        this.$toast.error("Something went wrong!");
                        console.log("ERROR addSOWFile", err);
                    })
                    .finally(() => (this.isLoading = false));
            } catch (err) {
                console.error("Error in SOW.vue - onSaveSow", err);
            }
        },
        onHandleUploadLater() {
            this.is_has_signed_sow = null;
            this.is_upload_later = true;
        },
    },
};
</script>
